/**
 * @file: utils.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 */

import { TaskListItem } from '@euler/model/TaskListItem';
import { CommonTaskStatus, InspectionTaskStatus } from '@euler/model/enum';
import { dateFromValue } from '@euler/utils/datetime';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import { StatusBadgeInfo } from './types';

export const computeVehicleAge = (
  modelYear: number | undefined,
): number | null => {
  if (!modelYear || modelYear < 1900) return null;
  const currentYear = new Date().getFullYear();
  const age = currentYear - modelYear;
  return age >= 0 ? age : null;
};

export const computeInShopDuration = (
  orderedAt: string | Date | undefined | null,
  finishedAt: string | Date | undefined | null,
): string => {
  if (!orderedAt) return '-';
  const end = finishedAt ? dateFromValue(finishedAt) : new Date();
  const start = dateFromValue(orderedAt);
  const totalMinutes = Math.max(differenceInMinutes(end, start), 0);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  if (days <= 0) return `${hours}时`;
  if (hours <= 0) return `${days}天`;
  return `${days}天${hours}时`;
};

export const formatMileage = (km: number): string => {
  if (!km || km <= 0) return '-';
  return `${km}公里`;
};

/** The subset of a task `deriveStatusBadge` actually reads. */
export type StatusBadgeInput = Pick<
  TaskListItem,
  | 'isRollback'
  | 'status'
  | 'isMainTask'
  | 'hasChildTasks'
  | 'preInspectionStatus'
  | 'inspectionStatus'
  | 'constructionStatus'
  | 'isConstructionJobScheduled'
  | 'deliveryCheckStatus'
>;

export const deriveStatusBadge = (
  task: StatusBadgeInput,
): StatusBadgeInfo | null => {
  if (task.isRollback) return { label: '返单中', palette: 'red' };
  if (task.status === InspectionTaskStatus.Cancelled) {
    return { label: '已取消', palette: 'amber' };
  }
  if (task.status === InspectionTaskStatus.Finished) {
    return { label: '已完工', palette: 'green' };
  }
  if (task.status !== InspectionTaskStatus.InProgress) {
    return { label: '已建单', palette: 'gray' };
  }
  if (task.isMainTask && task.hasChildTasks) {
    return { label: '进行中', palette: 'violet' };
  }
  if (task.deliveryCheckStatus === CommonTaskStatus.Finished) {
    return { label: '已质检', palette: 'green' };
  }
  if (task.deliveryCheckStatus === CommonTaskStatus.InProgress) {
    return { label: '质检中', palette: 'violet' };
  }
  if (task.constructionStatus === CommonTaskStatus.Finished) {
    return { label: '待质检', palette: 'violet' };
  }
  if (
    task.constructionStatus === CommonTaskStatus.InProgress ||
    task.isConstructionJobScheduled
  ) {
    return { label: '施工中', palette: 'cyan' };
  }
  if (task.inspectionStatus === CommonTaskStatus.Finished) {
    return { label: '待施工', palette: 'cyan' };
  }
  if (task.inspectionStatus === CommonTaskStatus.InProgress) {
    return { label: '检测中', palette: 'blue' };
  }
  if (task.preInspectionStatus === CommonTaskStatus.InProgress) {
    return { label: '预检中', palette: 'blue' };
  }
  if (task.preInspectionStatus === CommonTaskStatus.Finished) {
    return { label: '待检测', palette: 'blue' };
  }
  return { label: '已建单', palette: 'gray' };
};
